<script setup lang="ts">
import { NotificationType, User } from '@/models/user.model';
import usersService from '@/services/users.service';
import { Actions, ShiftCMSState } from '@/store';
import { PropType, reactive, ref } from 'vue';
import { useStore } from 'vuex';

const store = useStore<ShiftCMSState>();

const props = defineProps({
  user: {
    type: Object as PropType<User>,
    required: true,
  },
});

const showForm = ref<boolean>(false);
const showConfirmation = ref<boolean>(false);
const showError = ref<boolean>(false);

const notification = reactive({
  title: '',
  description: '',
});

async function sendNotification() {
  if (!showForm.value) {
    showForm.value = true;
    return;
  }
  if (confirm('are you sure you want to send this notification to the user?')) {
    const response = await usersService.sendNotification({
      userId: props.user.id,
      ...notification,
    });

    if (response.status) {
      notification.title = '';
      notification.description = '';
      showForm.value = false;
      showConfirmation.value = true;
      showError.value = false;
    } else {
      showForm.value = false;
      showError.value = true;
    }
  }

  setTimeout(() => {
    showForm.value = false;
    showConfirmation.value = false;
    showError.value = false;
  }, 5000);
}

async function testPopularInitiative() {
  if (
    confirm('are you sure you want to send this test notification to the user?')
  ) {
    await usersService.sendPopularTestNotification({
      userId: props.user.id,
    });
    store.dispatch(Actions.ShowNotification, {
      message: `Notification sent`,
      type: 'success',
    });
  }
}

async function sendDiscountNotification() {
  if (
    confirm('are you sure you want to send this test notification to the user?')
  ) {
    await usersService.sendDiscountNotification({
      userId: props.user.id,
    });
    store.dispatch(Actions.ShowNotification, {
      message: `Notification sent`,
      type: 'success',
    });
  }
}

async function sendPopularLearningItemNotification() {
  if (
    confirm('are you sure you want to send this test notification to the user?')
  ) {
    await usersService.sendPopularLearningItemNotification({
      userId: props.user.id,
    });
    store.dispatch(Actions.ShowNotification, {
      message: `Notification sent`,
      type: 'success',
    });
  }
}

async function sendPopularNotification() {
  if (
    confirm('are you sure you want to send this test notification to the user?')
  ) {
    await usersService.sendPopularNotification({
      userId: props.user.id,
    });
    store.dispatch(Actions.ShowNotification, {
      message: `Notification sent`,
      type: 'success',
    });
  }
}

async function sendQuizNotification() {
  if (
    confirm('are you sure you want to send this test notification to the user?')
  ) {
    await usersService.sendQuizNotification({
      userId: props.user.id,
    });
    store.dispatch(Actions.ShowNotification, {
      message: `Notification sent`,
      type: 'success',
    });
  }
}

async function sendDiscountEmail() {
  if (confirm('are you sure you want to send this test email to the user?')) {
    await usersService.sendDiscountEmail({
      userId: props.user.id,
    });
    store.dispatch(Actions.ShowNotification, {
      message: `Email sent`,
      type: 'success',
    });
  }
}

async function sendPopularEmail() {
  if (confirm('are you sure you want to send this test email to the user?')) {
    await usersService.sendPopularEmail({
      userId: props.user.id,
    });
    store.dispatch(Actions.ShowNotification, {
      message: `Email sent`,
      type: 'success',
    });
  }
}

async function sendPopularLearningItemEmail() {
  if (confirm('are you sure you want to send this test email to the user?')) {
    await usersService.sendPopularLearningItemEmail({
      userId: props.user.id,
    });
    store.dispatch(Actions.ShowNotification, {
      message: `Email sent`,
      type: 'success',
    });
  }
}

async function sendQuizEmail() {
  if (confirm('are you sure you want to send this test email to the user?')) {
    await usersService.sendQuizEmail({
      userId: props.user.id,
    });
    store.dispatch(Actions.ShowNotification, {
      message: `Email sent`,
      type: 'success',
    });
  }
}

function sendEmail(type: NotificationType) {
  switch (type) {
    case NotificationType.Discounts:
      return sendDiscountEmail();
    case NotificationType.PopulairInitiatives:
      return sendPopularEmail();
    case NotificationType.PopulairLearningItem:
      return sendPopularLearningItemEmail();
    case NotificationType.QuizStreak:
      return sendQuizEmail();
  }
}

function sendTestNotification(type: NotificationType) {
  switch (type) {
    case NotificationType.Discounts:
      return sendDiscountNotification();
    case NotificationType.PopulairInitiatives:
      return sendPopularNotification();
    case NotificationType.PopulairLearningItem:
      return sendPopularLearningItemNotification();
    case NotificationType.QuizStreak:
      return sendQuizNotification();
  }
}

const types = [
  NotificationType.Discounts,
  NotificationType.PopulairInitiatives,
  NotificationType.PopulairLearningItem,
  NotificationType.QuizStreak,
];
</script>
<template>
  <v-card class="mt-4" v-if="user.id !== -1">
    <v-card-title>Notifications and Email Settings</v-card-title>
    <v-card-text>
      <v-row>
        <v-col><strong>Type</strong></v-col>
        <v-col align="center"><strong>Email</strong></v-col>
        <v-col align="center"><strong>Test</strong></v-col>
        <v-col align="center"><strong>Phone</strong></v-col>
        <v-col align="center"><strong>Test</strong></v-col>
      </v-row>
      <v-row dense v-for="type in types" :key="type">
        <v-col alignSelf="center">
          <strong>{{
            $t(`user.notifications.${type.replace(/-/g, '_')}`)
          }}</strong>
        </v-col>
        <v-col align="center">
          <v-chip color="primary">{{
            user.notificationSettings.find((t) => t.notificationType === type)
              ?.email
              ? $t(`user.notifications.yes`)
              : $t(`user.notifications.no`)
          }}</v-chip>
        </v-col>
        <v-col align="center">
          <v-btn
            @click="sendEmail(type)"
            density="compact"
            icon
            color="info"
            :disabled="!user.email"
          >
            <v-icon icon="mdi-email" />
          </v-btn>
        </v-col>
        <v-col align="center">
          <v-chip color="primary">{{
            user.notificationSettings.find((t) => t.notificationType === type)
              ?.phone
              ? $t(`user.notifications.yes`)
              : $t(`user.notifications.no`)
          }}</v-chip> </v-col
        ><v-col align="center">
          <v-btn
            @click="sendTestNotification(type)"
            density="compact"
            icon
            color="info"
            :disabled="!(user.enablePushNotifications && user.expoPushToken)"
          >
            <v-icon icon="mdi-phone" /> </v-btn
        ></v-col>
      </v-row>

      <v-alert
        class="mt-4"
        type="warning"
        v-if="!(user.enablePushNotifications && user.expoPushToken)"
        >This user has no token or has notifications turned off.</v-alert
      >

      <v-form @submit.prevent="" v-if="showForm" class="mt-4">
        <v-text-field label="Title" v-model="notification.title" />
        <v-text-field
          label="Description"
          v-model="notification.description"
          :singleLine="false"
        />
      </v-form>
      <v-alert class="mb-4" v-if="showConfirmation" type="success"
        >The message was sent.</v-alert
      >
      <v-alert class="mb-4" v-if="showError" type="error"
        >The message could not be sent</v-alert
      >
      <v-btn
        color="info"
        density="compact"
        @click="sendNotification"
        v-if="user.enablePushNotifications && user.expoPushToken"
        >Send notification</v-btn
      >
      <v-btn
        class="ml-4"
        color="info"
        density="compact"
        @click="testPopularInitiative"
        v-if="user.enablePushNotifications && user.expoPushToken"
        >Test popular initiative</v-btn
      >
    </v-card-text>
  </v-card>
</template>
